import React, { useEffect, useState } from "react";
import Select from "react-select";
import PageHeader from "../../../components/common/PageHeader";
import { toast } from "react-toastify";
import "../styles/bulkTransfer.css";

import {
  getVerticals,
  getResponsibleUsers,
  getInquiriesForTransfer,
  bulkTransferInquiries,
} from "../api/bulkTransfer.api";
import BulkTransferTable from "../components/BulkTransferTable";
import ListPagination from "../../../components/common/ListPagination";
import { DEFAULT_PAGE_SIZE } from "../../../components/common/paginationUtils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

type SelectOption = {
  label: string;
  value: number | string;
};

const roleOptions: SelectOption[] = [
  { label: "Manager", value: "Manager" },
  { label: "Executive", value: "Executive" },
];

const BulkTransfer: React.FC = () => {
  /* ================= STATE ================= */

  const [verticalOptions, setVerticalOptions] = useState<SelectOption[]>([]);
  const [responsibleOptions, setResponsibleOptions] = useState<SelectOption[]>([]);

  const [vertical, setVertical] = useState<SelectOption | null>(null);
  const [role, setRole] = useState<SelectOption | null>(null);
  const [fromUser, setFromUser] = useState<SelectOption | null>(null);

  const MySwal = withReactContent(Swal);
  const [list, setList] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);   // ⭐ ADD THIS

  const [hasGenerated, setHasGenerated] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);

  /* ================= LOAD VERTICALS ================= */

  useEffect(() => {
    const loadVerticals = async () => {
      try {
        const res = await getVerticals();

        setVerticalOptions(
          (res.data.results || []).map((v: any) => ({
            label: v.name,
            value: v.id,
          }))
        );
      } catch {
        toast.error("Failed to load verticals");
      }
    };

    loadVerticals();
  }, []);

  /* ================= LOAD RESPONSIBLE USERS ================= */

  useEffect(() => {
    if (!vertical || !role) {
      setResponsibleOptions([]);
      return;
    }

    const loadUsers = async () => {
      try {
        const res = await getResponsibleUsers({
          vertical_id: vertical.value as number,
          role: role.value as string,
        });

        setResponsibleOptions(
          (res.data.data || []).map((u: any) => ({
            label: u.name,
            value: u.id,
          }))
        );
      } catch {
        toast.error("Failed to load responsible users");
      }
    };

    loadUsers();
  }, [vertical, role]);

  /* ================= FETCH ENQUIRIES ================= */

  const fetchData = async (pageNo: number) => {
    if (!vertical || !role || !fromUser) {
      toast.warning("Please select vertical, role and user");
      return;
    }

    try {
      setLoading(true);

      const res = await getInquiriesForTransfer(
        pageNo,
        vertical.value as number,
        fromUser.label as string,
        pageSize
      );
      setList(res.data.results || []);
      setTotalCount(res.data.count || 0);
      setHasGenerated(true);
    } catch {
      toast.error("Failed to fetch enquiries");
    } finally {
      setLoading(false);
    }
  };
  /* ================= PAGINATION ================= */

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    if (hasGenerated) {
      fetchData(page);
    }
  }, [page, pageSize]);


  /* ================= CHECKBOX LOGIC ================= */

  const handleSelect = (id: number) => {
    if (isSelectAll) {
      // unselect from global selection
      setSelectedIds(prev =>
        prev.includes(id) ? prev : [...prev, id]
      );
    } else {
      setSelectedIds(prev =>
        prev.includes(id)
          ? prev.filter(item => item !== id)
          : [...prev, id]
      );
    }
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      setIsSelectAll(false);
      setSelectedIds([]);
    } else {
      setIsSelectAll(true);
      setSelectedIds([]);
    }
  };

  const handleTransfer = async () => {
    if (!isSelectAll && selectedIds.length === 0) {
      toast.warning("Please select at least one inquiry");
      return;
    }

    if (!vertical || !fromUser) {
      toast.warning("Missing required data");
      return;
    }

    const MySwal = withReactContent(Swal);

    const TransferContent = () => {
      const [selectedRole, setSelectedRole] = React.useState<SelectOption | null>(null);
      const [selectedUser, setSelectedUser] = React.useState<SelectOption | null>(null);
      const [users, setUsers] = React.useState<SelectOption[]>([]);
      const [loadingUsers, setLoadingUsers] = React.useState(false);
      const [noUsersMessage, setNoUsersMessage] = React.useState<string>("");

      const handleRoleChange = async (role: any) => {
        setSelectedRole(role);
        setSelectedUser(null);
        setUsers([]);
        setNoUsersMessage("");

        if (!role) return;

        try {
          setLoadingUsers(true);

          const res = await getResponsibleUsers({
            vertical_id: vertical.value as number,
            role: role.value as string,
          });

          const fetchedUsers = res.data.data || [];

          const filtered = fetchedUsers.filter(
            (u: any) => u.name !== fromUser.label
          );

          if (filtered.length === 0) {
            setUsers([]);
            setNoUsersMessage("No users available for selected role.");
          } else {
            setUsers(
              filtered.map((u: any) => ({
                label: u.name,
                value: u.id,
              }))
            );
          }
        } catch {
          setUsers([]);
          setNoUsersMessage("Failed to load users.");
        } finally {
          setLoadingUsers(false);
        }
      };
      const count = isSelectAll ? totalCount : selectedIds.length;

      return (
        <div style={{ textAlign: "left" }}>
          <div style={{
            background: "#f8f9fa",
            padding: "12px 15px",
            borderRadius: "6px",
            marginBottom: "10px",
            fontSize: "14px"
          }}>
            You are transferring <strong>{count}</strong> {count > 1 ? "inquiries" : "inquiry"}
            from <strong>{fromUser.label}</strong> in <strong>{vertical.label}</strong> vertical.
          </div>
          <div style={{ marginTop: 30, marginBottom: 30 }}>
            <label className="form-label fw-semibold">Select Role</label>
            <Select
              options={roleOptions}
              placeholder="Select Role"
              isSearchable
              value={selectedRole}
              onChange={handleRoleChange}
              menuPlacement="auto"
              menuShouldScrollIntoView={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              maxMenuHeight={180}

              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                  pointerEvents: "auto",   // ✅ THIS FIXES IT
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}

            />
          </div>

          <div style={{ marginTop: 20 }}>
            <label className="form-label fw-semibold">Select User</label>
            <Select
              options={users}
              placeholder="Select User"
              isSearchable
              value={selectedUser}
              isLoading={loadingUsers}
              isDisabled={!selectedRole || users.length === 0}
              onChange={(user) => setSelectedUser(user as SelectOption)}
              menuPlacement="auto"
              menuShouldScrollIntoView={false}

              menuPortalTarget={document.body}
              menuPosition="fixed"
              maxMenuHeight={180}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                  pointerEvents: "auto",   // ✅ THIS FIXES IT
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />

            {noUsersMessage && (
              <div style={{ marginTop: 6, fontSize: 13, color: "#dc3545" }}>
                {noUsersMessage}
              </div>
            )}
          </div>

          {/* Hidden input for Swal preConfirm */}
          <input
            type="hidden"
            id="selectedUserHidden"
            value={selectedUser?.value || ""}
          />
        </div>
      );
    };

    const result = await MySwal.fire({
      title: "Transfer Inquiries",
      width: 800,
      heightAuto: false,
      html: <TransferContent />,
      showCancelButton: true,
      confirmButtonText: "Transfer",
      customClass: {
        actions: "transfer-popup-wrap",
        popup: "transfer-popup",
        confirmButton: "btn btn-primary px-4",
        cancelButton: "btn btn-secondary px-4 mx-2",
      },
      buttonsStyling: false,
      focusConfirm: false,
      preConfirm: () => {
        const selectedUserValue = (
          document.getElementById("selectedUserHidden") as HTMLInputElement
        ).value;

        if (!selectedUserValue) {
          Swal.showValidationMessage("Please select valid user");
          return;
        }

        return Number(selectedUserValue);
      },
    });

    if (!result.isConfirmed) return;
    // 🔹 SECOND CONFIRMATION
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to transfer selected inquiries. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Transfer",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545", // red danger color
      customClass: {
        confirmButton: "btn btn-danger px-4 mx-2",
        cancelButton: "btn btn-secondary px-4",
      },
      buttonsStyling: false,
    });

    if (!confirmResult.isConfirmed) return;


    try {
      setLoading(true);

if (isSelectAll) {
  await bulkTransferInquiries(
    {
      select_all: true,              // ✅ IMPORTANT
      transfer_to: result.value,
      reason: "Load balancing",
      confirm: true,
    },
    {
      vertical: vertical.value as number,
      assigned_to: fromUser.label,  // ✅ string
    }
  );
} else {
  await bulkTransferInquiries({
    enquiry_ids: selectedIds,
    transfer_to: result.value,
    reason: "Load balancing",
    confirm: true,
  });
}
      await Swal.fire({
        icon: "success",
        title: "Transferred Successfully",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchData(page);
      setSelectedIds([]);
    } catch {
      Swal.fire("Error", "Transfer failed", "error");
    } finally {
      setLoading(false);
    }
  };
  /* ================= CLEAR ================= */

  const handleClear = () => {
    setVertical(null);
    setRole(null);
    setFromUser(null);
    setResponsibleOptions([]);
    setList([]);
    setSelectedIds([]);
    setHasGenerated(false);
    setPage(1);
    setTotalCount(0);
  };

  /* ================= UI ================= */

  return (
    <>
      <PageHeader
        title="Bulk Inquiry Transfer"
        breadcrumbs={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "Bulk Transfer" },
        ]}
      />

      <div className="container mt-4">
        {/* ================= FILTER CARD ================= */}
        <div className="card p-4">
          <div className="row g-3">
            {/* Vertical */}
            <div className="col-md-3 stage-list">
              <label className="form-label fw-semibold">Vertical</label>
              <Select
                options={verticalOptions}
                value={vertical}
                onChange={(v) => {
                  setVertical(v);
                  setRole(null);
                  setFromUser(null);
                }}
                placeholder="Select Vertical"
              />
            </div>

            {/* Role */}
            <div className="col-md-3 stage-list">
              <label className="form-label fw-semibold">Role</label>
              <Select
                options={roleOptions}
                value={role}
                onChange={(v) => {
                  setRole(v);
                  setFromUser(null);
                }}
                placeholder="Select Role"
                isDisabled={!vertical}
              />
            </div>

            {/* From User */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">
                Select User (From)
              </label>
              <Select
                options={responsibleOptions}
                value={fromUser}
                onChange={(v) => setFromUser(v)}
                placeholder="Select User"
                isDisabled={!vertical || !role}
              />
            </div>
            <div className="col-md-3  mt-4 d-flex justify-content-end gap-2 align-items-end">
              <button
                className="btn btn-outline-secondary"
                onClick={handleClear}
              >
                Clear
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setSelectedIds([]);
                  setIsSelectAll(false);
                  setPage(1);
                  fetchData(1);
                }}
                disabled={loading}
              >
                Show
              </button>
            </div>
          </div>
        </div>



        {/* ================= TABLE ================= */}
        <div className="card p-4 mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span>
              {isSelectAll
                ? `All ${totalCount} inquiries selected`
                : selectedIds.length > 0 && `${selectedIds.length} Selected`}
            </span>

            <button
              className="btn btn-primary"
              onClick={handleTransfer}
              disabled={(!isSelectAll && selectedIds.length === 0) || loading}
            >
              Transfer Task
            </button>
          </div>

          <BulkTransferTable
            data={list}
            selectedIds={selectedIds}
            isSelectAll={isSelectAll}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
          />

          <ListPagination
            page={page}
            totalCount={totalCount}
            pageSize={pageSize}
            currentCount={list.length}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            visible={hasGenerated}
            className="p-3"
          />
        </div>
      </div>
    </>
  );
};

export default BulkTransfer;